import React, { useState } from 'react';
import { 
  Crown, Shield, Building, Users, GraduationCap, ArrowRight, 
  Lock, Key, Sparkles, CheckCircle2, AlertCircle, Mail, ChevronRight, LogOut
} from 'lucide-react';
import { UserSession } from '../AppShell';

interface FounderLoginPageProps {
  onLogin: (user: UserSession) => void;
  onLockOut: () => void;
}

export const FounderLoginPage: React.FC<FounderLoginPageProps> = ({ onLogin, onLockOut }) => {
  const [activeMode, setActiveMode] = useState<'one_click' | 'credentials'>('one_click');
  const [customEmail, setCustomEmail] = useState('');
  const [customPassword, setCustomPassword] = useState('');
  const [customRole, setCustomRole] = useState<'FOUNDER' | 'SUPER_ADMIN' | 'TENANT_ADMIN' | 'THERAPIST' | 'STUDENT'>('FOUNDER');
  const [customError, setCustomError] = useState('');
  const [loadingPersona, setLoadingPersona] = useState<string | null>(null);

  const all31ModulesList = [
    'aac', 'speech-therapy', 'autism', 'sign-language', 'braille', 
    'student', 'therapist', 'teacher', 'assessment', 'school-erp', 
    'hospital', 'billing', 'communication', 'inventory', 'analytics', 
    'ai', 'sensory-room', 'tele-rehab', 'early-intervention', 'vocational-living', 
    'hydrotherapy', 'music-therapy', 'animal-therapy', 'prosthetics',
    'vbmapp-assessment', 'core-vocab-ai', 'fim-weefim', 'evv-gps-tracker',
    'gamified-neuro', 'transition-matrix', 'audiometer'
  ];

  const personas = [
    {
      id: 'founder',
      title: 'Founder & Chief Executive Officer',
      badge: 'EXECUTIVE COMMAND',
      badgeColor: 'bg-amber-400 text-slate-950 border-amber-300',
      icon: Crown,
      iconBg: 'bg-gradient-to-tr from-amber-500 to-amber-300 text-slate-950',
      borderColor: 'border-amber-500/50 hover:border-amber-400 ring-1 ring-amber-500/30',
      cardBg: 'bg-gradient-to-br from-slate-900 via-slate-900 to-amber-950/40',
      email: 'founder@bugfreesolution.com',
      role: 'FOUNDER',
      name: 'Digvijay Yenurkar (Founder & CEO)',
      tenant: 'AbilityOS Global Holdings & Founder Cockpit',
      description: 'Strategic executive cockpit with real-time ARR/MRR ($1.84M), 142-tenant dynamic provisioning, multi-year version roadmap (v1.0–v2.5), and PHP 8.5 health telemetry.',
      destination: 'Founder Strategic Command Cockpit',
      buttonText: '⚡ 1-Click Login as Founder & CEO',
      btnStyle: 'bg-gradient-to-r from-amber-400 via-amber-300 to-amber-400 hover:from-amber-300 hover:to-amber-200 text-slate-950 shadow-amber-500/30 font-black',
      featured: true
    },
    {
      id: 'superadmin',
      title: 'Super Admin (Global Root WHM)',
      badge: 'INFRASTRUCTURE ROOT',
      badgeColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
      icon: Shield,
      iconBg: 'bg-emerald-600 text-white',
      borderColor: 'border-emerald-500/30 hover:border-emerald-400',
      cardBg: 'bg-slate-900/90',
      email: 'superadmin@bugfreesolution.com',
      role: 'SUPER_ADMIN',
      name: 'Global Root Administrator',
      tenant: 'WHM / cPanel MultiPHP 8.5 Root',
      description: 'System control plane with AlmaLinux WHM server telemetry, MariaDB 11.4 cross-tenant database syncing, and global platform configuration.',
      destination: 'Super Admin System HQ',
      buttonText: '⚡ 1-Click Login as Super Admin',
      btnStyle: 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-emerald-600/30 font-bold',
      featured: false
    },
    {
      id: 'school_admin',
      title: 'School Admin (Demo Org)',
      badge: 'TENANT ERP',
      badgeColor: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
      icon: Building,
      iconBg: 'bg-blue-600 text-white',
      borderColor: 'border-blue-500/30 hover:border-blue-400',
      cardBg: 'bg-slate-900/90',
      email: 'demo@bugfreesolution.com',
      role: 'TENANT_ADMIN',
      name: 'Demo Organization Principal',
      tenant: 'BugFree Solutions Demo Organization',
      description: 'Special education organization management with student enrollment rosters, therapist allocations, NDIS billing claims, and IEP audit tracker.',
      destination: 'School Admin Workspace',
      buttonText: '⚡ 1-Click Login as School Admin',
      btnStyle: 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-600/30 font-bold',
      featured: false
    },
    {
      id: 'therapist',
      title: 'Senior Clinical Therapist (SLP/OT)',
      badge: 'CLINICAL CARE',
      badgeColor: 'bg-purple-500/20 text-purple-300 border-purple-500/40',
      icon: Users,
      iconBg: 'bg-purple-600 text-white',
      borderColor: 'border-purple-500/30 hover:border-purple-400',
      cardBg: 'bg-slate-900/90',
      email: 'therapist@bugfreesolution.com',
      role: 'THERAPIST',
      name: 'Sarah Jenkins (SLP Specialist)',
      tenant: 'BugFree Special School',
      description: 'Clinical workspace with SOAP notes, articulation therapy, speech spectrogram, VB-MAPP assessments, and IEP milestone tracking.',
      destination: 'Therapist Clinical Workspace',
      buttonText: '⚡ 1-Click Login as Lead Therapist',
      btnStyle: 'bg-purple-600 hover:bg-purple-500 text-white shadow-purple-600/30 font-bold',
      featured: false
    },
    {
      id: 'student',
      title: 'Special Learner / Student Portal',
      badge: 'INCLUSIVE SELF-SERVICE',
      badgeColor: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
      icon: GraduationCap,
      iconBg: 'bg-amber-500 text-slate-950',
      borderColor: 'border-amber-500/30 hover:border-amber-400',
      cardBg: 'bg-slate-900/90',
      email: 'student@bugfreesolution.com',
      role: 'STUDENT',
      name: 'Master Rahul Sharma',
      tenant: 'BugFree Special School',
      description: 'Inclusive self-service portal with Text-to-Speech audio reader, 5-star token reward system, mood check-ins, and specialized disability accommodations.',
      destination: 'Student Self-Service Portal',
      buttonText: '⚡ 1-Click Login as Student',
      btnStyle: 'bg-amber-500 hover:bg-amber-400 text-slate-950 shadow-amber-500/30 font-black',
      featured: false
    }
  ];

  const handleExecuteQuickLogin = (persona: typeof personas[0]) => {
    setLoadingPersona(persona.id);
    setTimeout(() => {
      onLogin({
        name: persona.name,
        email: persona.email,
        role: persona.role,
        tenant: persona.tenant,
        subscriptionActive: true,
        enabledModules: all31ModulesList
      });
    }, 350);
  };

  const handleCustomSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customEmail) {
      setCustomError('Please enter an executive email address.');
      return;
    }

    const matchedPersona = personas.find(p => p.email.toLowerCase() === customEmail.toLowerCase());
    if (matchedPersona) {
      handleExecuteQuickLogin(matchedPersona);
      return;
    }

    // Default executive session
    onLogin({
      name: customEmail.split('@')[0].toUpperCase(),
      email: customEmail,
      role: customRole,
      tenant: 'BugFree Special School',
      subscriptionActive: true,
      enabledModules: all31ModulesList
    });
  };

  return (
    <div className="min-h-[85vh] max-w-7xl mx-auto pt-28 pb-16 px-4 space-y-8 animate-fade-in">
      
      {/* Top Banner with Founder Security Status */}
      <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-indigo-950 text-white p-6 md:p-8 rounded-3xl border border-amber-500/40 shadow-2xl relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-72 h-72 bg-amber-500/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-blue-500/15 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/20 text-amber-300 text-xs font-black rounded-full border border-amber-500/40 uppercase tracking-widest">
              <Sparkles className="w-3.5 h-3.5 text-amber-400" />
              <span>Founder PIN 3606 Verified &bull; Isolated Executive Gateway</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight text-white">
              Executive 1-Click Persona Login Gateway
            </h1>
            <p className="text-xs md:text-sm text-slate-300 max-w-3xl leading-relaxed">
              Authorized access point for Founders, System Administrators, Therapists, and Students. All 1-click switches have been completely removed from public views and are exclusively available here.
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => setActiveMode(activeMode === 'one_click' ? 'credentials' : 'one_click')}
              className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 font-bold rounded-xl text-xs flex items-center gap-2 transition"
            >
              <Key className="w-3.5 h-3.5 text-amber-400" />
              <span>{activeMode === 'one_click' ? 'Manual Sign In' : 'Show 1-Click Cards'}</span>
            </button>
            <button
              onClick={onLockOut}
              className="px-4 py-2.5 bg-red-950/60 hover:bg-red-900 border border-red-500/40 text-red-200 font-bold rounded-xl text-xs flex items-center gap-2 transition"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Lock &amp; Exit</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mode 1: 1-Click Persona Cards Grid */}
      {activeMode === 'one_click' && (
        <div className="space-y-6">
          
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <h2 className="text-xl font-black text-slate-900 flex items-center gap-2">
                <span>Select a Persona for Instant 1-Click Access</span>
              </h2>
              <p className="text-xs text-slate-500">
                Click any role to immediately sign in and enter that respective workspace without entering credentials.
              </p>
            </div>
            <div className="hidden sm:flex items-center gap-2 text-[11px] font-mono font-bold bg-amber-50 text-amber-900 px-3 py-1.5 rounded-xl border border-amber-200">
              <Lock className="w-3.5 h-3.5 text-amber-600" />
              <span>ROUTE: /founder [AUTHORIZED]</span>
            </div>
          </div>

          {/* Grid of 5 personas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            
            {personas.map((persona) => {
              const IconComponent = persona.icon;
              const isLoading = loadingPersona === persona.id;

              return (
                <div
                  key={persona.id}
                  className={`${persona.cardBg} ${persona.borderColor} ${
                    persona.featured ? 'lg:col-span-2' : ''
                  } rounded-3xl p-6 border shadow-xl flex flex-col justify-between transition-all hover:scale-[1.01] hover:shadow-2xl relative overflow-hidden`}
                >
                  {persona.featured && (
                    <div className="absolute top-0 right-0 transform translate-x-8 -translate-y-8 w-32 h-32 bg-amber-500/10 rounded-full blur-2xl pointer-events-none" />
                  )}

                  <div className="space-y-4">
                    
                    {/* Card Header */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black shadow-lg ${persona.iconBg}`}>
                          <IconComponent className="w-6 h-6" />
                        </div>
                        <div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${persona.badgeColor}`}>
                            {persona.badge}
                          </span>
                          <h3 className="text-lg font-black text-white leading-tight mt-1">
                            {persona.title}
                          </h3>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-xs text-slate-300 leading-relaxed">
                      {persona.description}
                    </p>

                    {/* Metadata Pill Box */}
                    <div className="bg-slate-950/60 rounded-xl p-3 border border-slate-800/80 space-y-1.5 text-xs font-mono">
                      <div className="flex items-center justify-between text-slate-400">
                        <span>Email:</span>
                        <span className="text-amber-300 font-bold">{persona.email}</span>
                      </div>
                      <div className="flex items-center justify-between text-slate-400">
                        <span>Role:</span>
                        <span className="text-emerald-400 font-bold">{persona.role}</span>
                      </div>
                      <div className="flex items-center justify-between text-slate-400">
                        <span>Target:</span>
                        <span className="text-blue-300 font-bold">{persona.destination}</span>
                      </div>
                    </div>

                  </div>

                  {/* Launch Action Button */}
                  <div className="pt-5 mt-5 border-t border-slate-800/80">
                    <button
                      type="button"
                      disabled={isLoading}
                      onClick={() => handleExecuteQuickLogin(persona)}
                      className={`w-full py-3 px-4 rounded-xl text-xs flex items-center justify-center gap-2 shadow-lg transition active:scale-95 ${persona.btnStyle}`}
                    >
                      <Sparkles className="w-4 h-4 fill-current" />
                      <span>{isLoading ? 'Authenticating...' : persona.buttonText}</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>

                </div>
              );
            })}

          </div>

        </div>
      )}

      {/* Mode 2: Manual Executive Credentials Form */}
      {activeMode === 'credentials' && (
        <div className="max-w-md mx-auto bg-slate-900 border border-slate-800 rounded-3xl p-8 text-white shadow-2xl space-y-6">
          <div className="text-center space-y-1.5">
            <div className="w-12 h-12 bg-amber-500/20 text-amber-300 rounded-2xl flex items-center justify-center mx-auto border border-amber-500/40">
              <Key className="w-6 h-6" />
            </div>
            <h2 className="text-2xl font-black">Executive Sign In</h2>
            <p className="text-xs text-slate-400">
              Enter specific executive credentials or switch to a custom role.
            </p>
          </div>

          {customError && (
            <div className="p-3 bg-red-950/60 border border-red-500/40 rounded-xl text-red-300 text-xs font-bold flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{customError}</span>
            </div>
          )}

          <form onSubmit={handleCustomSubmit} className="space-y-4 text-xs">
            <div>
              <label className="block text-slate-300 font-bold mb-1">Target Persona Role</label>
              <select
                value={customRole}
                onChange={(e) => setCustomRole(e.target.value as any)}
                className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white font-bold focus:ring-2 focus:ring-amber-500 focus:outline-none"
              >
                <option value="FOUNDER">👑 Founder &amp; CEO (Founder Dashboard)</option>
                <option value="SUPER_ADMIN">🛡️ Super Admin (Global Root WHM)</option>
                <option value="TENANT_ADMIN">🏫 School / Tenant Admin</option>
                <option value="THERAPIST">🩺 Senior Clinical Therapist</option>
                <option value="STUDENT">🎓 Special Learner / Student</option>
              </select>
            </div>

            <div>
              <label className="block text-slate-300 font-bold mb-1">Executive Email Address</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-500 absolute left-3 top-3" />
                <input
                  type="email"
                  required
                  value={customEmail}
                  onChange={(e) => setCustomEmail(e.target.value)}
                  placeholder="founder@bugfreesolution.com"
                  className="w-full pl-9 pr-3 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white font-mono focus:ring-2 focus:ring-amber-500 focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-slate-300 font-bold mb-1">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-3" />
                <input
                  type="password"
                  value={customPassword}
                  onChange={(e) => setCustomPassword(e.target.value)}
                  placeholder="••••••••••••"
                  className="w-full pl-9 pr-3 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white font-mono focus:ring-2 focus:ring-amber-500 focus:outline-none"
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full py-3 bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-slate-950 font-black rounded-xl shadow-lg shadow-amber-500/20 transition flex items-center justify-center gap-2"
            >
              <Key className="w-4 h-4" />
              <span>Authorize &amp; Enter Workspace</span>
            </button>
          </form>

          <div className="text-center pt-2">
            <button
              type="button"
              onClick={() => setActiveMode('one_click')}
              className="text-xs text-amber-400 hover:underline font-bold"
            >
              &larr; Return to 1-Click Persona Cards
            </button>
          </div>
        </div>
      )}

    </div>
  );
};
